import { useEffect, useState } from 'react';

import { Box, IconButton, Typography, styled, Alert, Dialog } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { CancelButton, SubmitButton } from 'components/ui/buttons';

function SortableItem({ item, getItemId, getItemLabel, loading }) {
    const itemId = getItemId(item);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: itemId,
        disabled: loading,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: isDragging ? undefined : transition,
    };

    return (
        <Box ref={setNodeRef} style={style}>
            <ItemRow $dragging={isDragging} {...attributes} {...listeners}>
                <DragHandle disabled={loading} aria-label={`Drag ${getItemLabel(item)}`}>
                    <DragHandleIcon />
                </DragHandle>
                <Box minWidth={0} pr={2}>
                    <ItemLabel>{getItemLabel(item)}</ItemLabel>
                </Box>
            </ItemRow>
        </Box>
    );
}

export default function DisplayOrderModal(props) {
    const {
        open,
        items,
        getItemId,
        getItemLabel,
        onClose,
        onSave,
        loading = false,
        description = 'Drag and drop items to change their display order.',
    } = props;

    const [orderedItems, setOrderedItems] = useState(items);
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 6,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    useEffect(() => {
        if (open === true) {
            setOrderedItems(items);
        }
    }, [items, open]);

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) {
            return;
        }

        setOrderedItems((currentItems) => {
            const oldIndex = currentItems.findIndex((item) => getItemId(item) === active.id);
            const newIndex = currentItems.findIndex((item) => getItemId(item) === over.id);

            if (oldIndex === -1 || newIndex === -1) {
                return currentItems;
            }

            return arrayMove(currentItems, oldIndex, newIndex);
        });
    };

    const handleSave = () => {
        onSave(orderedItems.map((item) => getItemId(item)));
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <Box m={2}>
                <Alert severity="info">{description}</Alert>
            </Box>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={orderedItems.map((item) => getItemId(item))}
                    strategy={verticalListSortingStrategy}
                >
                    <Box>
                        {orderedItems.map((item) => (
                            <SortableItem
                                key={getItemId(item)}
                                item={item}
                                getItemId={getItemId}
                                getItemLabel={getItemLabel}
                                loading={loading}
                            />
                        ))}
                    </Box>
                </SortableContext>
            </DndContext>

            <ActionBar>
                <CancelButton disabled={loading} onClick={onClose}>
                    Cancel
                </CancelButton>
                <SubmitButton disabled={loading} loading={loading} onClick={handleSave}>
                    Save
                </SubmitButton>
            </ActionBar>
        </Dialog>
    );
}

const ItemRow = styled(Box, {
    shouldForwardProp: (prop) => prop !== '$dragging',
})(({ theme, $dragging }) => ({
    backgroundColor: $dragging ? theme.palette.grey[100] : '#fff',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottomWidth: 0.5,
    borderBottomColor: theme.palette.grey[300],
    borderBottomStyle: 'solid',
    boxShadow: $dragging ? theme.shadows[3] : 'none',
    cursor: 'grab',
}));

const ItemLabel = styled(Typography)(({ theme }) => ({
    fontSize: 18,
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: 2,
}));

const ActionBar = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
}));

const DragHandle = styled(IconButton)(({ theme }) => ({
    color: theme.palette.grey[500],
    cursor: 'grab',
    flexShrink: 0,
    touchAction: 'none',
    marginRight: theme.spacing(1),
}));

DragHandle.defaultProps = { size: 'small', disableRipple: true };

const DragHandleIcon = styled(DragIndicatorIcon)(({ theme }) => ({
    color: theme.palette.grey[400],
}));
